// Selector hooks widgets call instead of useWebSocket. Each acquires a
// refcount on its coin (driving the shared subscription) and subscribes
// to that coin's slice of the store. Re-renders are scoped per-coin
// rather than per-connection — changing BTC trades doesn't re-render
// ETH-bound widgets.

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { tradeStore } from './tradeStore';
import { acquireCoin, releaseCoin } from './tradeStream';

function subscribeForCoin(coin) {
  return (cb) => tradeStore.subscribe(coin, cb);
}

// useSyncExternalStore requires a stable snapshot — same reference if
// nothing changed. Per-coin `version` int gives us cheap referential
// equality via a memoised wrapper.
function makeGetSnapshot(coin, lastRef) {
  return () => {
    const slice = tradeStore.getSlice(coin);
    if (lastRef.current?.version === slice.version) return lastRef.current;
    lastRef.current = slice;
    return slice;
  };
}

// Drives the shared subscription. Idempotent across mount/unmount and
// coin changes.
function useCoinSubscription(coin) {
  useEffect(() => {
    if (!coin) return;
    acquireCoin(coin);
    return () => releaseCoin(coin);
  }, [coin]);
}

export function useCoinTrades(coin) {
  useCoinSubscription(coin);
  const lastRef = useRef(null);
  // Re-create the subscribe/getSnapshot pair when coin changes so
  // useSyncExternalStore swaps to the new listener set.
  const slice = useSyncExternalStore(
    subscribeForCoin(coin),
    makeGetSnapshot(coin, lastRef),
    makeGetSnapshot(coin, lastRef)
  );
  return slice; // { whaleTrades, liquidations, summary, version }
}

// Convenience accessors — let widgets read just what they need so the
// React re-render only fires when *their* field changes. Both share the
// same underlying useCoinTrades, but selectors at the call-site keep
// shallow-equality compare cheap.
export function useCoinSummary(coin) {
  return useCoinTrades(coin).summary;
}

export function useCoinWhaleTrades(coin) {
  return useCoinTrades(coin).whaleTrades;
}

export function useCoinLiquidations(coin) {
  return useCoinTrades(coin).liquidations;
}
